import React, { useState, useEffect } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { IndianRupee, CheckCircle, Clock, CreditCard, Save, Pencil, Calendar } from "lucide-react";

export default function AgentSalaryPage() {
  const { user } = useAuth();
  const [salary, setSalary] = useState(null);
  const [bankDetails, setBankDetails] = useState({ bank_name: "", account_number: "", ifsc_code: "", account_holder: "" });
  const [bankSaved, setBankSaved] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const month = (() => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit'
    });
    return formatter.format(new Date());
  })(); // Current month only in IST

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await apiCall("get", `/agent/my-salary?month=${month}`);
        setSalary(data);
        if (data.bank_details?.account_number) {
          setBankDetails(prev => ({ ...prev, ...data.bank_details }));
          setBankSaved(true);
          setEditingBank(false);
        }
      } catch { toast.error("Failed"); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const saveBankDetails = async () => {
    if (!bankDetails.account_number || !bankDetails.bank_name) { toast.error("Fill bank name and account"); return; }
    setSaving(true);
    try {
      await apiCall("post", "/salary/bank-details", { ...bankDetails, agent_id: user?._id || user?.id });
      toast.success("Saved");
      setBankSaved(true);
      setEditingBank(false);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="agent-salary-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>My Salary - {month}</h2>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border border-border"><CardContent className="p-3 flex items-center gap-2"><IndianRupee className="w-5 h-5 text-primary shrink-0" /><div><p className="text-[10px] text-muted-foreground uppercase">Monthly</p><p className="text-base sm:text-lg font-bold">{salary?.monthly_salary || 0} INR</p></div></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-3 flex items-center gap-2">{salary?.payment_status === "paid" ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" /> : <Clock className="w-5 h-5 text-yellow-500 shrink-0" />}<div><p className="text-[10px] text-muted-foreground uppercase">Status</p><p className="text-base sm:text-lg font-bold capitalize">{salary?.payment_status || "Pending"}</p></div></CardContent></Card>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Card className="border border-border"><CardContent className="p-2 text-center"><p className="text-[9px] text-muted-foreground uppercase">Present</p><p className="text-lg font-bold text-green-600">{salary?.present || 0}</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-2 text-center"><p className="text-[9px] text-muted-foreground uppercase">Absent</p><p className="text-lg font-bold text-red-500">{salary?.absent || 0}</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-2 text-center"><p className="text-[9px] text-muted-foreground uppercase">Half</p><p className="text-lg font-bold text-yellow-600">{salary?.half_day || 0}</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="p-2 text-center"><p className="text-[9px] text-muted-foreground uppercase">W.Off</p><p className="text-lg font-bold text-blue-500">{salary?.week_off || 0}</p></CardContent></Card>
      </div>

      {/* Bank Details - minimal after save */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Bank</CardTitle>
            {bankSaved && !editingBank && <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setEditingBank(true)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {bankSaved && !editingBank ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{bankDetails.bank_name}</span>
                <span className="text-xs text-muted-foreground">****{bankDetails.account_number?.slice(-4)}</span>
              </div>
              {bankDetails.updated_at && <span className="text-[10px] text-muted-foreground">{bankDetails.updated_at?.split("T")[0]}</span>}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Holder</Label><Input value={bankDetails.account_holder} onChange={e => setBankDetails({ ...bankDetails, account_holder: e.target.value })} className="h-8 text-sm" /></div>
                <div><Label className="text-[10px]">Bank</Label><Input value={bankDetails.bank_name} onChange={e => setBankDetails({ ...bankDetails, bank_name: e.target.value })} className="h-8 text-sm" /></div>
                <div><Label className="text-[10px]">A/C No.</Label><Input value={bankDetails.account_number} onChange={e => setBankDetails({ ...bankDetails, account_number: e.target.value })} className="h-8 text-sm" /></div>
                <div><Label className="text-[10px]">IFSC</Label><Input value={bankDetails.ifsc_code} onChange={e => setBankDetails({ ...bankDetails, ifsc_code: e.target.value })} className="h-8 text-sm" /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={saveBankDetails} disabled={saving}><Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}</Button>
                {bankSaved && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditingBank(false)}>Cancel</Button>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attendance Calendar */}
      {salary?.attendance && salary.attendance.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-1"><CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Attendance</CardTitle></CardHeader>
          <CardContent className="pt-1">
            <div className="grid grid-cols-7 gap-1">
              {["M","T","W","T","F","S","S"].map((d,i) => <div key={i} className="text-center text-[9px] font-semibold text-muted-foreground">{d}</div>)}
              {salary.attendance.map((a, i) => {
                const c = { present: "bg-green-500/20 text-green-700 dark:text-green-400", absent: "bg-red-500/20 text-red-700 dark:text-red-400", half_day: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400", week_off: "bg-blue-500/20 text-blue-700 dark:text-blue-400" };
                return <div key={i} className={`text-center text-[10px] py-1 rounded ${c[a.status] || "bg-muted"}`} title={`${a.date}: ${a.status}${a.hours ? ` (${a.hours}h)` : ""}`}>{parseInt(a.date.split("-")[2])}</div>;
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-green-500/20" />Present</span>
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-red-500/20" />Absent</span>
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-yellow-500/20" />Half</span>
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-blue-500/20" />W.Off</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
